import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "./verification_pros.css"; // on réutilise le style existant

// ⚙️ Mets à true si ton backend envoie l'email dans /pro-validation (recommandé)
// Mets à false si tu veux que le front appelle /send-email en complément (fallback)
const EMAIL_SENT_SERVER_SIDE = true;

/* ===== Base API robuste (prod & dev) =====
   - Essaie VITE_API_URL / REACT_APP_API_URL
   - Nettoie les slashs finaux + /api éventuel
   - Sinon, retombe sur le même domaine que le front
*/
const RAW =
  (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_URL) ||
  process.env.VITE_API_URL ||
  process.env.REACT_APP_API_URL ||
  "";

const API_BASE = (RAW
  ? RAW.replace(/\/+$/, "").replace(/\/api$/i, "")
  : (typeof window !== "undefined" && window.location.origin) || ""
);

function VerificationPros() {
  const navigate = useNavigate();

  const [pros, setPros] = useState([]);
  const [loading, setLoading] = useState(true);

  // msg global en haut (succès/erreur)
  const [msg, setMsg] = useState("");

  // popup refus
  const [refuseId, setRefuseId] = useState(null);
  const [refuseComment, setRefuseComment] = useState("");

  // état d’action par ligne (pour éviter multi-clic)
  const [processing, setProcessing] = useState({ id: null, type: null }); // type: "validate" | "refuse" | null
  const [missionAccess, setMissionAccess] = useState({});

  const token = localStorage.getItem("token");
  const role = localStorage.getItem("role");

  // sécurité minimale : si pas admin -> home
  useEffect(() => {
    if (!token || role !== "admin") navigate("/");
    // eslint-disable-next-line
  }, []);

  const fetchPros = useCallback(async () => {
    setLoading(true);
    setMsg("");
    try {
      const res = await fetch(`${API_BASE}/api/admin/pros-en-attente`, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        const list = Array.isArray(data) ? data : [];
        setPros(list);
        setMissionAccess(
          list.reduce((acc, p) => {
            acc[p.userId] = p.missionsAccess === true;
            return acc;
          }, {})
        );
      } else setMsg(data.error || "Erreur lors du chargement.");
    } catch {
      setMsg("Erreur serveur.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchPros();
  }, [fetchPros]);

  // Fallback d’envoi email si ton backend ne l’envoie pas encore
  const sendEmail = async (to, subject, text) => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/send-email`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify({ to, subject, text }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.error || `HTTP ${res.status}`);
      }
      return true;
    } catch (err) {
      console.error("Erreur lors de l'envoi de l'email:", err);
      return false;
    }
  };

  const handleValidate = async (userId) => {
    setMsg("");
    setProcessing({ id: userId, type: "validate" });
    try {
      const res = await fetch(`${API_BASE}/api/admin/pro-validation/${userId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify({
          action: "valide",
          grantMissionAccess: missionAccess[userId] === true,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setMsg(data?.error || "Erreur lors de l'opération.");
        return;
      }

      // Si le serveur envoie déjà l'email → afficher le vrai statut
      if (EMAIL_SENT_SERVER_SIDE) {
        setMsg(
          data?.emailSent
            ? `✅ Professionnel validé${missionAccess[userId] === true ? " avec accès missions" : " sans accès missions"}. ✉️ Email envoyé.`
            : `✅ Professionnel validé${missionAccess[userId] === true ? " avec accès missions" : " sans accès missions"}. ⚠️ Email non envoyé.`
        );
      } else {
        // Fallback : envoi email côté front (déconseillé, mais dispo)
        const pro = pros.find((p) => p.userId === userId);
        const to = pro?.email;
        if (!to) {
          setMsg("✅ Professionnel validé, mais ✖️ email introuvable pour l'envoi.");
        } else {
          const emailText =
            "Félicitations ! Votre compte professionnel a été validé. Vous pouvez maintenant accéder à toutes les fonctionnalités de notre plateforme.";
          const ok = await sendEmail(
            to,
            "Validation de votre compte professionnel",
            emailText
          );
          setMsg(
            ok
              ? "✅ Professionnel validé ! ✉️ Email envoyé."
              : "✅ Professionnel validé, mais ✖️ l'email n'a pas pu être envoyé."
          );
        }
      }

      // Retire la carte du pro validé
      setPros((prev) => prev.filter((p) => p.userId !== userId));
    } catch {
      setMsg("Erreur serveur.");
    } finally {
      setProcessing({ id: null, type: null });
    }
  };

  const handleRefuse = async (userId) => {
    setMsg("");
    setProcessing({ id: userId, type: "refuse" });
    try {
      const res = await fetch(`${API_BASE}/api/admin/pro-validation/${userId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify({ action: "refuse", commentaire: refuseComment }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setMsg(data?.error || "Erreur lors de l'opération.");
        return;
      }

      if (EMAIL_SENT_SERVER_SIDE) {
        setMsg(
          data?.emailSent
            ? "❌ Professionnel refusé. ✉️ Motif envoyé."
            : "❌ Professionnel refusé. ⚠️ Email non envoyé."
        );
      } else {
        const pro = pros.find((p) => p.userId === userId);
        const to = pro?.email;
        if (!to) {
          setMsg("❌ Professionnel refusé, mais ✖️ email introuvable pour l'envoi.");
        } else {
          const emailText = `Désolé, votre demande de validation a été refusée. Motif : ${refuseComment}`;
          const ok = await sendEmail(
            to,
            "Refus de validation de votre compte professionnel",
            emailText
          );
          setMsg(
            ok
              ? "❌ Professionnel refusé. ✉️ Email envoyé."
              : "❌ Professionnel refusé, mais ✖️ l'email n'a pas pu être envoyé."
          );
        }
      }

      // Clean popup + retirer la carte
      setPros((prev) => prev.filter((p) => p.userId !== userId));
      setRefuseId(null);
      setRefuseComment("");
    } catch {
      setMsg("Erreur serveur.");
    } finally {
      setProcessing({ id: null, type: null });
    }
  };

  const openProtectedDocument = async (relativeUrl) => {
    if (!relativeUrl) return;
    try {
      const res = await fetch(`${API_BASE}${relativeUrl}`, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Impossible d'ouvrir le document.");
      }
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      window.open(objectUrl, "_blank", "noopener,noreferrer");
      setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
    } catch (e) {
      setMsg(String(e.message || "Erreur ouverture document"));
    }
  };

  const isProcessing = (id) => processing.id === id;

  return (
    <div className="verif-admin-container">
      <h2>Vérification des professionnels</h2>

      {msg && (
        <div
          className={`verif-admin-msg ${
            msg.startsWith("✅") ? "success" : msg.startsWith("❌") ? "error" : "info"
          }`}
        >
          {msg}
        </div>
      )}

      {loading ? (
        <div className="verif-admin-loading">Chargement...</div>
      ) : pros.length === 0 ? (
        <div className="verif-admin-empty">Aucun professionnel en attente.</div>
      ) : (
        <div className="verif-annonces-list">
          {pros.map((p) => (
            <div className="verif-annonce-card" key={p.userId}>
              {/* En-tête */}
              <div className="verif-card-head">
                <div className="verif-card-title">
                  {p.companyName ||
                    `${p.prenom || ""} ${p.nom || ""}`.trim() ||
                    "(Nom manquant)"}
                </div>
                <div className="verif-card-city">
                  {p.ville} {p.postalCode}
                </div>
              </div>

              {/* Infos principales */}
              <div className="verif-card-info">
                <b>SIRET :</b> {p.siret || "—"}
                <br />
                <b>Email :</b> {p.email || "—"}
                <br />
                <b>Téléphone :</b> {p.telephone || "—"}
              </div>

              {/* Liens documents (URLs signées côté API idéalement) */}
              <div className="verif-docs" style={{ marginTop: 8 }}>
                <div className="verif-doc-line">
                  <b>Kbis / RNE :</b>{" "}
                  {p.docs?.kbisUrl ? (
                    <button
                      type="button"
                      className="verif-doc-btn"
                      onClick={() => openProtectedDocument(p.docs.kbisUrl)}
                    >
                      Voir
                    </button>
                  ) : (
                    "—"
                  )}
                </div>
                <div className="verif-doc-line">
                  <b>Pièce d’identité :</b>{" "}
                  {p.docs?.idCardUrl ? (
                    <button
                      type="button"
                      className="verif-doc-btn"
                      onClick={() => openProtectedDocument(p.docs.idCardUrl)}
                    >
                      Voir
                    </button>
                  ) : (
                    "—"
                  )}
                </div>
                <div className="verif-doc-line">
                  <b>Justificatif d’adresse :</b>{" "}
                  {p.docs?.proofAddressUrl ? (
                    <button
                      type="button"
                      className="verif-doc-btn"
                      onClick={() => openProtectedDocument(p.docs.proofAddressUrl)}
                    >
                      Voir
                    </button>
                  ) : (
                    "—"
                  )}
                </div>
              </div>

              <label className="verif-mission-access">
                <input
                  type="checkbox"
                  checked={missionAccess[p.userId] === true}
                  onChange={(e) =>
                    setMissionAccess((prev) => ({
                      ...prev,
                      [p.userId]: e.target.checked,
                    }))
                  }
                />
                <span>
                  Autoriser ce pro à poster des <strong>missions ProMeet</strong>
                  <small>
                    Si la case est cochée et que le compte est validé, le bouton ProMeet Missions apparaît dans son menu.
                    Sinon il ne voit rien et ne peut pas poster de mission.
                  </small>
                </span>
              </label>

              {/* Actions */}
              <div className="verif-card-btns">
                <button
                  className="verif-btn approve"
                  onClick={() => handleValidate(p.userId)}
                  disabled={isProcessing(p.userId)}
                  title={
                    isProcessing(p.userId) ? "Action en cours..." : "Valider"
                  }
                >
                  {isProcessing(p.userId) && processing.type === "validate"
                    ? "Validation..."
                    : "Valider"}
                </button>

                <button
                  className="verif-btn refuse"
                  onClick={() => setRefuseId(p.userId)}
                  disabled={isProcessing(p.userId)}
                  title={
                    isProcessing(p.userId) ? "Action en cours..." : "Refuser"
                  }
                >
                  Refuser
                </button>
              </div>

              {/* Popup de refus */}
              {refuseId === p.userId && (
                <div className="verif-refuse-popup">
                  <textarea
                    value={refuseComment}
                    onChange={(e) => setRefuseComment(e.target.value)}
                    placeholder="Motif du refus (sera envoyé au professionnel)"
                    rows={3}
                    style={{ width: "100%", marginTop: 8 }}
                  />
                  <div style={{ marginTop: 6, display: "flex", gap: 8 }}>
                    <button
                      className="verif-btn refuse"
                      onClick={() => handleRefuse(p.userId)}
                      disabled={!refuseComment.trim() || isProcessing(p.userId)}
                    >
                      {isProcessing(p.userId) && processing.type === "refuse"
                        ? "Refus..."
                        : "Confirmer le refus"}
                    </button>
                    <button
                      className="verif-btn"
                      onClick={() => {
                        setRefuseId(null);
                        setRefuseComment("");
                      }}
                      disabled={isProcessing(p.userId)}
                    >
                      Annuler
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default VerificationPros;
